import classNames from 'classnames';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DemoPill } from '@/components/ui/controls';
import { NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, Card, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { useSegments } from '@/utils/use-segments';

import type { Channel, Message, MessageDetail, Trigger } from './api-types';
import { channelName, describeDelay, describeEvent } from './channels';
import MessagingNav from './messaging-nav';
import { ChannelList, ChannelPicker, MessageStatusBadge, SegmentPicker, TriggerIcon, messageDate } from './parts';

const TITLE_MAX = 120;

/** The quick composer from the scope doc's mockup: a title, channels and segments, then review (OUT-1, SEG-3). */
const NewMessageCard = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['email', 'telegram']);
  const [segments, setSegments] = useState<string[]>([]);
  // The draft this form made: saving again updates it, never makes a second one.
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const ready = Boolean(title.trim() && channels.length && segments.length);

  // Both buttons store a draft; review then asks for the text the quick form has no room for.
  const save = async (thenReview: boolean) => {
    setBusy(true);
    setFailed(false);
    const body = { title: title.trim(), body: '', channels, segment_ids: segments };
    try {
      const { data } = draft ? await http.put<MessageDetail>(`/messages/${draft}`, body) : await http.post<MessageDetail>('/messages', body);
      setDraft(data.id);
      if (thenReview) navigate(`/messaging/messages/${data.id}/review`);
      else setSaved(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="New message"
      aside={
        <Link to="/messaging/messages/new" className={btn.link}>
          Open the full editor
        </Link>
      }
    >
      <label className={field.label} htmlFor="quick-title">
        Message title
      </label>
      <div className="relative">
        <input
          id="quick-title"
          className={classNames(field.input, 'pr-20')}
          maxLength={TITLE_MAX}
          placeholder="New feature announcement"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setSaved(false);
          }}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums text-fg-faint">
          {title.length} / {TITLE_MAX}
        </span>
      </div>

      <p className={classNames(field.label, 'mt-4')}>Channels</p>
      <ChannelPicker value={channels} onChange={setChannels} withNotes={false} />

      <SegmentPicker className="mt-4" value={segments} onChange={setSegments} />

      <div className="mt-5 flex flex-col gap-3 border-t border-line pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <button type="button" className={classNames(btn.primary, 'w-full sm:w-auto')} disabled={!ready || busy} onClick={() => save(true)}>
          Review &amp; send
          <span aria-hidden>→</span>
        </button>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {saved && (
            <span role="status" className="text-sm text-fg-muted">
              Saved as a draft.{' '}
              <Link to={`/messaging/messages/${draft}/edit`} className={btn.link}>
                Add the text
              </Link>
            </span>
          )}
          {failed && <span className="text-sm text-danger">That did not work. Try again.</span>}
          <button type="button" className={classNames(btn.secondary, 'w-full sm:w-auto')} disabled={!title.trim() || busy} onClick={() => save(false)}>
            Save as draft
          </button>
        </div>
      </div>
    </Card>
  );
};

/** Automated messages, sent when something happens (OUT-3). */
const TriggersCard = () => {
  const { data, error } = useApi<Trigger[]>('/triggers');
  const { byId } = useSegments();
  return (
    <Card
      title="Automated triggers"
      aside={
        <Link to="/messaging/triggers" className={btn.link}>
          Manage triggers
        </Link>
      }
    >
      {error && !data && <p className="text-sm text-danger">Triggers could not be loaded.</p>}
      {data && data.length === 0 && <p className="text-sm text-fg-faint">No triggers yet.</p>}
      <ul className="space-y-2">
        {(data ?? []).slice(0, 5).map((t) => (
          <li key={t.id}>
            <Link
              to={`/messaging/triggers/${t.id}`}
              className="flex items-start gap-3 rounded-lg bg-surface-inset px-3 py-3 transition-colors hover:bg-surface-hover md:items-center md:py-2.5"
            >
              <span className={classNames('shrink-0', !t.evaluated && notYet)}>
                <TriggerIcon event={t.event} />
              </span>
              <span className={classNames('min-w-0 flex-1', !t.evaluated && notYet)}>
                <span className="block break-words text-sm text-fg-strong md:truncate">
                  {describeEvent(t)} → {t.template_name}
                </span>
                <span className="block break-words text-xs text-fg-muted md:truncate">
                  {t.segment_ids.length ? t.segment_ids.map((id) => byId(id)?.name ?? 'a deleted segment').join(', ') : 'Everyone it happens to'} ·{' '}
                  {describeDelay(t.delay_hours).toLowerCase()} · {t.channels.map(channelName).join(', ')}
                </span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1 md:flex-row md:items-center md:gap-2">
                {/* Not in this version: faded, with the info mark beside the badge that says so. */}
                {!t.evaluated && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className={notYet}>
                      <Badge>Not evaluated yet</Badge>
                    </span>
                    <NotYetInfo what="trigger" nested />
                  </span>
                )}
                <span className={classNames(!t.evaluated && notYet)}>
                  <Badge tone={t.active ? 'ok' : 'neutral'}>{t.active ? 'Active' : 'Paused'}</Badge>
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <Link to="/messaging/triggers/new" className={classNames(btn.link, 'mt-3 inline-flex min-h-11 items-center md:min-h-0')}>
        + Add trigger
      </Link>
    </Card>
  );
};

/** The latest messages (OUT-2). */
const LatestCard = () => {
  const { data, error } = useApi<Message[]>('/messages');
  const latest = (data ?? []).slice(0, 6);
  return (
    <Card
      title="Latest messages"
      aside={
        <span className="flex items-center gap-3">
          {latest.some((m) => m.demo) && <DemoPill title="Some of these come from the local demo seed">Demo</DemoPill>}
          <Link to="/messaging/messages" className={btn.link}>
            All messages
          </Link>
        </span>
      }
    >
      {error && !data && <p className="text-sm text-danger">Messages could not be loaded.</p>}
      {data && latest.length === 0 && <p className="text-sm text-fg-faint">Nothing written yet.</p>}
      <ul className="divide-y divide-line">
        {latest.map((m) => (
          <li key={m.id} className="py-2.5 first:pt-0 last:pb-0">
            <Link to={`/messaging/messages/${m.id}`} className="group block py-1 md:py-0">
              <span className="flex items-start justify-between gap-3">
                <span className="min-w-0 break-words text-sm text-fg-strong group-hover:text-brand">{m.title}</span>
                <MessageStatusBadge status={m.status} />
              </span>
              <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
                <ChannelList channels={m.channels} iconsOnly />
                {messageDate(m)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
};

/** Messaging center (OUT-1…6, SEG-3), as in the scope doc's mockup: compose, automate, and see what went out. */
const MessagingPage = () => (
  <div>
    <PageHeader
      title="Messaging center"
      description="Create, automate and send updates to leads and casino owners by email, Telegram and Owner Studio notifications."
      actions={
        <Link to="/messaging/messages/new" className={btn.primary}>
          New message
        </Link>
      }
    >
      <MessagingNav />
    </PageHeader>
    <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4 md:space-y-6">
        <NewMessageCard />
        <TriggersCard />
      </div>
      <LatestCard />
    </div>
  </div>
);

export default MessagingPage;
